import _ from "lodash";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import {
  cardStyles,
  modalGenericStyles,
  tableStyles,
  variables,
} from "../../../../../variables";
import PropTypes from "prop-types";
import { retrieveMember } from "../../../../../api/CRM/Members/Members";
import { AddressesCard } from "../../../../../components/cards";
import { ContactsCard } from "../../../../../components/cards/contacts";
import GenericBasicInfo from "../../../../../components/cards/GenericBasicInfo";
import EntityPreferences from "../../../../../components/Entity/entity_preferences";
import { CommentsCard } from "../../../../../components/cards/comments";
import { DocumentsCard } from "../../../CommonCRMModals/Documents";
import { genericRequestErrorHandler } from "../../../../../api";
import { useQuery } from "@tanstack/react-query";
import Loader from "../../../../../components/Loader";
import CRMTravelServicesTable from "../../../CommonCRMModals/CRMTravelServicesTable";
import CRMGroupsTable from "../../../CommonCRMModals/CRMGroups";
import TabStrip from "../../../../../components/TabStrip";
import { CustomButton } from "../../../../../components/CustomButton";
import MemberLogsTab from "./MemberLogsTab";
import MemberBuyingStatments from "../../MemberBuyingStatments";
import MemberSellingStatments from "../../MemberSellingStatments";
import MemberProformaBuyingStatments from "../../MemberProformaBuyingStatments";
import MemberProformaSellingStatments from "../../MemberProformaSellingStatments";

const memberBasicInformation = createUseStyles({
  ...cardStyles,
  MemberBasicInfo: cardStyles.card,
  headCell: tableStyles.head.cell,
  headRow: tableStyles.head.row,
  tableCell: tableStyles.body.cell,
});

const MemberBasicInformation = ({ entity }) => {
  const classes = memberBasicInformation();
  return (
    <div id="DetailMemberModal" className={classes.MemberBasicInfo}>
      <div className={classes.header}>
        <span>Basic Information</span>
      </div>
      <div className={classes.body}>
        <table>
          <thead>
            <tr className={classes.headRow}>
              <th className={classes.headCell}>Id</th>
              <th className={classes.headCell}>Name</th>
              <th className={classes.headCell}>Email</th>
              <th className={classes.headCell}>Mobile Phone</th>
              <th className={classes.headCell}>Currency</th>
              <th className={classes.headCell}>Created</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={classes.tableCell}>{entity.id || "N/A"}</td>
              <td className={classes.tableCell}>{entity.name || "N/A"}</td>
              <td className={classes.tableCell}>{entity.email || "N/A"}</td>
              <td className={classes.tableCell}>
                {entity.mobile_phone || "N/A"}
              </td>
              <td className={classes.tableCell}>{entity.currency || "N/A"}</td>
              <td className={classes.tableCell}>
                {entity.created
                  ? DateTime.fromISO(entity.created).toLocaleString(
                      DateTime.DATETIME_MED_WITH_WEEKDAY
                    )
                  : "N/A"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
MemberBasicInformation.propTypes = {
  entity: PropTypes.object.isRequired,
};

const b2cVivaCredentialsType = {
  "": "-----",
  LV: "Live",
  DE: "Demo",
};

const initialfilters = {
  limit: 20,
  page: 1,
};
const styles = createUseStyles({
  ...modalGenericStyles,
  DetailsMemberModal: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content max-content 1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: { ...cardStyles.card, maxWidth: "100%" },
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
    maxWidth: "100%",
    overflow: "hidden",
  },
  firstRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridGap: variables.normal_gap,
    justifyContent: "left",
  },
  basicInfoCardBody: {
    extend: "cardBody",
    overflow: "auto",
  },
  basicInfoRightDetailsRowCardBody: {
    ...cardStyles.body,
    display: "grid",
    width: "100%",
    justifySelf: "center",
    justifyItems: "center",
    gridTemplateColumns: "repeat(1, 1fr)",
    gap: variables.double_gap,
    alignContent: "start",
  },
  seconRowcardBody: {
    extend: "cardBody",
    maxHeight: "15rem",
  },
  [`@media ${variables.media.smallscreen}`]: {
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
    },
    basicInfoRightDetailsRowCardBody: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  [`@media ${variables.media.bigscreen}`]: {
    firstRow: {
      gridTemplateColumns: "1fr 1fr",
    },
  },
});
const DetailsMemberModal = ({ memberId, onClose }) => {
  const classes = styles();
  const [activeTab, setActiveTab] = useState("basicInfo");

  const getWorkingDays = (weekdays) => {
    let workingDays = [];
    weekdays.forEach((weekDay, idx) => {
      let day = "";
      switch (idx) {
        case 0:
          if (weekdays[idx]) {
            day = "Monday";
          }
          break;
        case 1:
          if (weekdays[idx]) {
            day = "Tuesday ";
          }
          break;
        case 2:
          if (weekdays[idx]) {
            day = "Wednesday";
          }
          break;
        case 3:
          if (weekdays[idx]) {
            day = "Thursday";
          }
          break;
        case 4:
          if (weekdays[idx]) {
            day = "Friday";
          }
          break;
        case 5:
          if (weekdays[idx]) {
            day = "Saturday";
          }
          break;
        case 6:
          if (weekdays[idx]) {
            day = "Sunday";
          }
          break;
        default:
          day = "";
          break;
      }
      if (day) {
        workingDays.push(day);
      }
    });
    return workingDays;
  };

  const { data: member, isLoading: loading } = useQuery({
    queryKey: ["DetailMemberModal", memberId],
    queryFn: async () => await retrieveMember({ id: memberId }),
    onError: (error) => genericRequestErrorHandler(error),
  });

  const basicDataCpl = [
    ["Business Name", null, _.capitalize(_.get(member, "data.name"))],
    ["Legal Title", "legal_title"],
    ["Registration Code", "registration_code"],
    ["Founding Year", "founding_year"],
    ["Email", "email"],
    ["Mobile Phone", "mobile_phone"],
    ["Phone", "phone"],
    ["Contact Email", "contact_persons.0.email"],
    ["Completeness", null, `${_.get(member, "data.completeness")}%`],
    ["Url", "url"],
    ["Currency", "currency"],
  ];

  const PaymentGatewayCredentialsCpls = [
    ["Felloh Organisation", "felloh_organisation"],
    [
      "Felloh environment",
      null,
      _.startCase(_.get(member, "data.felloh_environment")),
    ],
    ["Felloh Public Key", "felloh_public_key"],
    ["Felloh Secret Key", "felloh_secret_key"],
    ["B2C Stripe Accepted Coupons", "b2c_stripe_accepted_coupons"],
    ["b2c Stripe API Public Key", "b2c_stripe_api_public_key"],
    ["B2C Stripe API Secret Key", "b2c_stripe_api_secret_key"],
    ["B2C Viva API Key", "b2c_viva_api_key"],
    [
      "B2C Viva Credentials Type",
      null,
      _.get(
        b2cVivaCredentialsType,
        _.get(member, "data.b2c_viva_credentials_type", "") || "N/A"
      ),
    ],
    ["B2C Viva Merchant Id", "b2c_viva_source_code"],
    ["Barclays Passphrase", "barclays_passphrase"],
    ["Barclays Passphrase", "barclays_passphrase"],
    ["Barclays Pspid", "barclays_pspid"],
  ];

  const basicCpl = [
    ["Timezone", "timezone"],
    [
      "Days",
      null,
      getWorkingDays(_.get(member, "data.weekdays", [])).join(", "),
    ],
  ];
  const menuTabs = {
    basicInfo: "Basic Info",
    comments: "Comments",
    buyingStatment: "Buying Statment",
    sellingStatment: "Selling Statment",
    proformaBuyingStatment: "Proforma Buying Statment",
    proformaSellingStatment: "Proforma Selling Statment",
    logs: "Logs",
    // documents: "Documents",
  };

  return (
    <div id="DetailMemberModal" className={classes.DetailsMemberModal}>
      {loading ? (
        <div className={classes.modalCard}>
          <div className={classes.modalCardHeader}>
            <span>Member Details</span>
          </div>
          <div className={classes.modalCardBody}>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={classes.modalCard}>
          <div className={classes.modalCardHeader}>
            <span>Member Details</span>
          </div>
          <TabStrip
            tabs={Object.entries(menuTabs)}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <div className={classes.modalCardBody}>
            {activeTab === "basicInfo" && (
              <React.Fragment>
                <div className={classes.card}>
                  <div className={classes.firstRow}>
                    <div className={classes.basicInfoCardBody}>
                      <GenericBasicInfo
                        header="Basic Data"
                        cardInfo={basicDataCpl}
                        objectData={_.get(member, "data", [])}
                      />
                    </div>

                    <div className={classes.basicInfoCardBody}>
                      <GenericBasicInfo
                        header="Payment Gateway Credentials"
                        cardInfo={PaymentGatewayCredentialsCpls}
                        objectData={_.get(member, "data", [])}
                      />
                    </div>
                  </div>
                </div>
                <div className={classes.card}>
                  <div className={classes.basicInfoRightDetailsRowCardBody}>
                    <div className={classes.seconRowcardBody}>
                      <GenericBasicInfo
                        header="Profile Operation Time"
                        cardInfo={basicCpl}
                        objectData={_.get(member, "data", [])}
                        custonStyles={{ header: { width: "100%" } }}
                      />
                    </div>
                    <div className={classes.seconRowcardBody}>
                      <CRMTravelServicesTable
                        data={_.get(member, "data.travel_services_display", [])}
                      />
                    </div>
                    <div className={classes.seconRowcardBody}>
                      <CRMGroupsTable
                        data={_.get(member, "data.groups_display", [])}
                      />
                    </div>
                  </div>
                </div>
                <EntityPreferences entity={_.get(member, "data", [])} />
                <AddressesCard
                  addresses={_.get(member, "data.addresses", [])}
                />
                <ContactsCard
                  contacts={_.get(member, "data.contact_persons", [])}
                />
              </React.Fragment>
            )}
            {activeTab === "comments" && (
              <CommentsCard title="Comments" id={memberId} modeType="member" />
            )}
            {activeTab === "buyingStatment" && (
              <MemberBuyingStatments entityData={_.get(member, "data", {})} />
            )}
            {activeTab === "sellingStatment" && (
              <MemberSellingStatments entityData={_.get(member, "data", {})} />
            )}
            {activeTab === "proformaBuyingStatment" && (
              <MemberProformaBuyingStatments
                entityData={_.get(member, "data", {})}
              />
            )}
            {activeTab === "proformaSellingStatment" && (
              <MemberProformaSellingStatments
                entityData={_.get(member, "data", {})}
              />
            )}
            {/* {activeTab === "depositStatments" && (
              <DepositStatments
                title="Deposit Statment"
                invalidationKey="DetailMemberModal"
                entityType="member"
                entityData={_.get(member, "data", {})}
              />
            )} */}
            {activeTab === "documents" && (
              <DocumentsCard
                title="Documents"
                id={memberId}
                showAddButton={true}
                sourceEntity={`${memberId}___member`}
              />
            )}
            {activeTab === "logs" && <MemberLogsTab />}
          </div>
          <div className={classes.modalCardActions}>
            <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
              <strong>Close</strong>
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};
DetailsMemberModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default DetailsMemberModal;
